import { ReactElement, useEffect, useMemo } from 'react';
import { AddActivityFormValues } from 'core/models';
import { CONTENT_TAG_CME_ROOT_ID } from 'core/constants';
import { FormService } from 'services/FormService';
import PillCheckbox from 'components/PillCheckbox/PillCheckbox';
import { useFormikContext } from 'formik';
import { sortBy } from 'lodash';
import FormikConditionalCheckboxes from 'components/forms/FormikConditionalCheckboxes/FormikConditionalCheckboxes';
import { useContentTags } from './contentTagHelpers';
import { useIsFirstRender } from 'hooks';
import { List } from '@mui/material';
import styles from './EditContentTags.module.scss';

export interface EditDetailContentTagProps {
  enabled: (values: AddActivityFormValues) => boolean;
  rootId: string;
  title: string;
  topTitle: string;
  childTitle: string;
}

export const EditCMEStateContentTag = ({
  rootId,
  title,
  topTitle,
  childTitle,
  enabled,
}: EditDetailContentTagProps): ReactElement => {
  const { contentTagsById, contentTagsByParentId } = useContentTags();

  const topTags = useMemo(
    () =>
      (contentTagsByParentId[rootId] || []).map((i) => ({
        Id: i.id,
        Title: i.contentTagName,
        Tooltip: i.tooltip,
      })),
    [contentTagsByParentId, rootId],
  );

  const { values, setFieldValue } = useFormikContext<AddActivityFormValues>();
  const topLevelKey = `contentTagsTop-${rootId}`;
  const childKey = 'contentTags';
  const selectedTopTags: string[] = useMemo(() => values[topLevelKey] || [], [values, topLevelKey]);
  const orderedSelectedTopTags: string[] = useMemo(() => {
    const tagsWithChildren = selectedTopTags.filter((id) => (contentTagsByParentId[id] || []).length > 0);
    const tagsWithoutChildren = selectedTopTags.filter((id) => (contentTagsByParentId[id] || []).length === 0);
    return [...tagsWithChildren, ...tagsWithoutChildren];
  }, [contentTagsByParentId, selectedTopTags]);

  // on load, set topLevelKey based on childKey initially
  const isFirstRender = useIsFirstRender();
  useEffect(() => {
    // });
    if (isFirstRender) {
      // first render, set topLevelKey based on childKey and top level tags
      const childTags: string[] = values[childKey] || [];
      const parentTags = childTags.reduce((acc, tagId) => {
        const tag = contentTagsById[tagId];
        if (!tag) {
          return acc;
        }
        if (!topTags.some((i) => i.Id === tag.parentContentTagId)) {
          return acc;
        }
        if (!acc.includes(tag.parentContentTagId)) {
          acc.push(tag.parentContentTagId);
        }
        return acc;
      }, [] as string[]);

      const stateContentTags = Array.isArray(values.contentTags) ? values.contentTags : [];
      const selectedTopLevelTags = stateContentTags.reduce((acc, tagId) => {
        if (!topTags.some((i) => i.Id === tagId)) {
          return acc;
        }
        if (!acc.includes(tagId)) {
          acc.push(tagId);
        }
        return acc;
      }, parentTags);

      setFieldValue(topLevelKey, selectedTopLevelTags);
    } else {
      // not first render, remove childKeys not in topLevelKey (ie. if the user deselects a top level item, uncheck it's children)
      const childTags: string[] = (values[childKey] || []).sort();
      const parentTags = selectedTopTags;
      const newChildTags = childTags
        .filter((tag) => parentTags.includes(contentTagsById[tag]?.parentContentTagId))
        .sort();

      // only update the data in formik if they are different
      if (JSON.stringify(childTags) !== JSON.stringify(newChildTags)) {
        setFieldValue(childKey, newChildTags);
      }
    }
  }, [isFirstRender, topTags, childKey, selectedTopTags, setFieldValue, topLevelKey, values, contentTagsById]);

  const hasAnyChildTags = orderedSelectedTopTags.some((id) => (contentTagsByParentId[id] || []).length > 0);

  if (!enabled(values)) {
    return <></>;
  }

  return (
    <div className="form-section">
      <div className="form-group form-group-title">
        {FormService.renderLabel({
          hasGroupTitle: true,
          name: topLevelKey,
          required: false,
          suppressLabel: true,
          title: title,
        })}
        <div className="form-group form-sub-group">
          {FormService.renderLabel({
            name: topLevelKey,
            required: true,
            suppressLabel: true,
            title: topTitle,
          })}
          <div className="form-input" role="group">
            <PillCheckbox name={topLevelKey} items={topTags} />
          </div>
        </div>
        {!!selectedTopTags.length && hasAnyChildTags && (
          <div className="form-group form-sub-group">
            {FormService.renderLabel({
              name: topLevelKey,
              required: true,
              suppressLabel: true,
              title: childTitle,
            })}
            {orderedSelectedTopTags.map((id) => {
              const parentTag = topTags.find((tag) => tag.Id === id);
              const childTags = sortBy(
                (contentTagsByParentId[id] || []).map((i) => ({
                  Id: i.id,
                  Title: i.contentTagName,
                  Tooltip: i.tooltip,
                })),
                'Title',
              );
              if (childTags.length) {
                return (
                  <>
                    <List key={`${childKey}-${id}`} className={styles.contentTagList}>
                      <h4>{parentTag?.Title}</h4>
                      <div className="form-input" role="group">
                        <FormikConditionalCheckboxes
                          options={childTags}
                          isRequired={false}
                          formikKey={childKey}
                          showTooltipIcon
                        />
                      </div>
                    </List>
                    <hr className={styles.separator} />
                  </>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export const EditContentTags = (): ReactElement => {
  return (
    <EditCMEStateContentTag
      rootId={CONTENT_TAG_CME_ROOT_ID}
      title="AMA PRA Category 1 Credit™"
      topTitle="Select the high-level content area(s) that your activity addresses."
      childTitle="Select the specific content area(s) that your activity addresses."
      enabled={(v) => v.hasStateContentTags === 'stateContentTagYes1'}
    />
  );
};
